import { useState, useEffect, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { sendDemoRequest } from '../lib/emailjs'

/* ── Types ─────────────────────────────────────────── */
type View = 'calendar' | 'form' | 'submitting' | 'thankyou' | 'error'
type FormSub = 'contact' | 'message'

/* ── Time utilities ────────────────────────────────── */
function parseMinutes(t: string): number {
  const [time, period] = t.trim().split(' ')
  let [h, m] = time.split(':').map(Number)
  if (period === 'PM' && h !== 12) h += 12
  if (period === 'AM' && h === 12) h = 0
  return h * 60 + m
}

function toTime(mins: number): string {
  const h = Math.floor(mins / 60)
  const m = mins % 60
  const p = h >= 12 ? 'PM' : 'AM'
  const hr = h > 12 ? h - 12 : h === 0 ? 12 : h
  return `${hr}:${m.toString().padStart(2, '0')} ${p}`
}

function generateSlots(ranges: [string, string][]): string[] {
  const slots: string[] = []
  for (const [start, end] of ranges) {
    let m = parseMinutes(start)
    const e = parseMinutes(end)
    while (m < e) { slots.push(toTime(m)); m += 30 }
  }
  return slots
}

function dk(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function getMonday(d: Date): Date {
  const r = new Date(d)
  const day = r.getDay()
  r.setDate(r.getDate() + (day === 0 ? 1 : 1 - day))
  r.setHours(0, 0, 0, 0)
  return r
}

function weekDays(mon: Date): Date[] {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(mon)
    d.setDate(d.getDate() + i)
    return d
  })
}

/* ── Timezone conversion ───────────────────────────── */
// Availability is defined in US Eastern time
const SOURCE_TZ = 'America/New_York'

function estToLocal(dateStr: string, timeStr: string): { time: string; tz: string } {
  // Build a Date in EST by parsing in that timezone
  const mins = parseMinutes(timeStr)
  const h = Math.floor(mins / 60)
  const m = mins % 60
  const iso = `${dateStr}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`

  // Create formatter in source TZ to get the UTC offset
  const srcFmt = new Intl.DateTimeFormat('en-US', {
    timeZone: SOURCE_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  })

  // Find the UTC time by creating the date as if in EST
  // We use a trick: format a known date in EST, then compute offset
  const estDate = new Date(iso + '-05:00') // EST is UTC-5, EDT is UTC-4
  // Check if it's EDT by seeing what the offset actually is
  const parts = srcFmt.formatToParts(estDate)
  const srcHour = Number(parts.find(p => p.type === 'hour')?.value ?? h)
  // If the hour doesn't match, we're in EDT — adjust
  if (srcHour !== h) {
    estDate.setTime(estDate.getTime() + (h - srcHour) * 3600000)
  }

  // Format in user's local timezone
  const localTime = estDate.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })

  const localTz = estDate.toLocaleTimeString('en-US', {
    timeZoneName: 'short',
  }).split(' ').pop() ?? ''

  return { time: localTime, tz: localTz }
}

/* ── Availability from Google Sheet ────────────────── */
// Edit availability at:
// https://docs.google.com/spreadsheets/d/1AGDAWOATh2eXLqY7ie5yOvDeHWxwGYSxyUkXiFIKqYw
//
// Sheet schema — first sheet (gid=0), header row + one row per day.
// Date column is flexible (YYYY-MM-DD, 4/7/2026, 4/7/26 all work).
// After Date, columns come in Start/End pairs. Leave trailing pairs blank
// when you only need one or two ranges. All times are US Eastern; the modal
// converts to each viewer's local tz.
//   Date       | Start Time | End Time | Start Time (2) | End Time (2) | ...
//   4/21/2026  | 11:00 AM   | 1:00 PM  | 3:00 PM        | 6:00 PM      |
//   4/23/2026  | 2:00 PM    | 5:00 PM  |                |              |
//
// The sheet must be shared "Anyone with the link can view".
const SHEET_ID = '1AGDAWOATh2eXLqY7ie5yOvDeHWxwGYSxyUkXiFIKqYw'
const SHEET_GID = '0'
const CSV_URL = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/export?format=csv&gid=${SHEET_GID}`

function parseCsvLine(line: string): string[] {
  const out: string[] = []
  let cur = ''
  let quoted = false
  for (let i = 0; i < line.length; i++) {
    const c = line[i]
    if (c === '"') {
      if (quoted && line[i + 1] === '"') { cur += '"'; i++ }
      else quoted = !quoted
    } else if (c === ',' && !quoted) {
      out.push(cur); cur = ''
    } else {
      cur += c
    }
  }
  out.push(cur)
  return out
}

function normalizeDate(s: string): string | null {
  const t = s.trim()
  if (!t) return null
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t
  const d = new Date(t)
  if (isNaN(d.getTime())) return null
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

async function fetchAvailability(): Promise<Map<string, string[]>> {
  const res = await fetch(CSV_URL, { cache: 'no-store' })
  if (!res.ok) throw new Error(`Sheet fetch failed: ${res.status}`)
  const csv = await res.text()
  const lines = csv.split(/\r?\n/).filter(l => l.trim().length > 0)
  const byDate = new Map<string, [string, string][]>()
  for (let i = 1; i < lines.length; i++) {
    const cols = parseCsvLine(lines[i]).map(c => c.trim())
    const [rawDate, ...rest] = cols
    const date = rawDate ? normalizeDate(rawDate) : null
    if (!date) continue
    const ranges = byDate.get(date) ?? []
    for (let k = 0; k + 1 < rest.length; k += 2) {
      const start = rest[k]
      const end = rest[k + 1]
      if (!start || !end) continue
      ranges.push([start, end])
    }
    if (ranges.length > 0) byDate.set(date, ranges)
  }
  const result = new Map<string, string[]>()
  byDate.forEach((ranges, date) => {
    result.set(date, generateSlots(ranges))
  })
  return result
}

// Module-level cache so reopens don't re-fetch
let availCache: Map<string, string[]> | null = null
let availPromise: Promise<Map<string, string[]>> | null = null

function loadAvailability(): Promise<Map<string, string[]>> {
  if (availCache) return Promise.resolve(availCache)
  if (availPromise) return availPromise
  availPromise = fetchAvailability()
    .then(m => { availCache = m; return m })
    .catch(err => { availPromise = null; throw err })
  return availPromise
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

/* ── Component ─────────────────────────────────────── */
export default function DemoModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [view, setView] = useState<View>('calendar')
  const [formSub, setFormSub] = useState<FormSub>('contact')
  const [weekOff, setWeekOff] = useState(0)
  const [selDay, setSelDay] = useState<string | null>(null)
  const [selTime, setSelTime] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [company, setCompany] = useState('')
  const [msg, setMsg] = useState('')

  const [avail, setAvail] = useState<Map<string, string[]>>(() => availCache ?? new Map())
  const [availLoading, setAvailLoading] = useState(false)

  // Load availability from Google Sheet when modal opens
  useEffect(() => {
    if (!open) return
    if (availCache) { setAvail(availCache); return }
    setAvailLoading(true)
    loadAvailability()
      .then(m => setAvail(m))
      .catch(err => { console.error('Availability fetch failed:', err) })
      .finally(() => setAvailLoading(false))
  }, [open])

  const thisMon = useMemo(() => getMonday(new Date()), [])
  const curMon = useMemo(() => {
    const d = new Date(thisMon)
    d.setDate(d.getDate() + weekOff * 7)
    return d
  }, [thisMon, weekOff])
  const days = useMemo(() => weekDays(curMon), [curMon])
  const weekLabel = `${MONTHS[curMon.getMonth()]} ${curMon.getDate()}`

  // Reset on open — jump to the week containing the next available day
  useEffect(() => {
    if (open) {
      setView('calendar'); setFormSub('contact')
      setSelTime(null)
      setName(''); setEmail(''); setCompany(''); setMsg('')

      const today = dk(new Date())
      // Find the earliest available date on or after today
      const upcoming = Array.from(avail.keys())
        .filter(d => d >= today)
        .sort()
      if (upcoming.length > 0) {
        const target = new Date(upcoming[0] + 'T12:00:00')
        const targetMon = getMonday(target)
        const diff = Math.round((targetMon.getTime() - thisMon.getTime()) / (7 * 86400000))
        setWeekOff(Math.max(0, Math.min(diff, 1)))
        setSelDay(upcoming[0])
      } else {
        setWeekOff(0)
        setSelDay(null)
      }
    }
  }, [open, avail, thisMon])

  // Auto-select first available day when navigating weeks
  useEffect(() => {
    if (!open) return
    const found = days.find(d => avail.has(dk(d)))
    setSelDay(found ? dk(found) : null)
  }, [weekOff, days, avail, open])

  // Escape to close
  useEffect(() => {
    if (!open) return
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [open, onClose])

  // Lock body scroll when modal open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = '' }
    }
  }, [open])

  const pickTime = (t: string) => { setSelTime(t); setView('form'); setFormSub('contact') }

  const submit = async () => {
    if (!name.trim() || !email.trim()) return
    setView('submitting')
    try {
      const localInfo = selDay && selTime ? estToLocal(selDay, selTime) : null
      const selDate = selDay ? new Date(selDay + 'T12:00:00') : null
      const dateFmt = selDate?.toLocaleDateString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
      }) ?? ''
      await sendDemoRequest({
        name: name.trim(),
        email: email.trim(),
        company: company.trim() || '(not provided)',
        message: msg.trim() || '(none)',
        date: dateFmt,
        time_et: selTime ?? '',
        time_local: localInfo?.time ?? '',
        time_tz: localInfo?.tz ?? '',
      })
      setView('thankyou')
    } catch (err) {
      console.error('Demo submit failed:', err)
      setView('error')
    }
  }

  const selDate = selDay ? new Date(selDay + 'T12:00:00') : null
  const dateFmt = selDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) ?? ''
  const slots = selDay ? avail.get(selDay) ?? [] : []
  const canPrev = weekOff > 0
  const canNext = weekOff < 1

  const inputCls = 'w-full rounded-xl border border-oak-black/10 bg-white px-4 py-3 text-[15px] tracking-[-0.2px] text-oak-black placeholder:text-oak-black/30 focus:border-oak-black/25 focus:outline-none transition-colors'

  return (
    <AnimatePresence initial={false}>
      {open && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[10010] bg-black/30 backdrop-blur-[10px]"
            onClick={onClose}
          />

          {/* Positioning wrapper */}
          <div className="pointer-events-none fixed inset-0 z-[10010] flex items-end justify-center p-3">
            <motion.div
              initial={{ opacity: 0, y: '100%' }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              className="pointer-events-auto flex max-h-[calc(100vh-24px)] w-full max-w-[640px] flex-col overflow-hidden rounded-3xl bg-white shadow-2xl"
            >
              {/* ── Header ─────────────────────── */}
              <div className="flex items-center gap-3 px-6 pb-1 pt-6">
                <AnimatePresence mode="wait">
                  {view === 'form' && formSub === 'message' && (
                    <motion.button
                      key="back"
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -8 }}
                      transition={{ duration: 0.15 }}
                      onClick={() => setFormSub('contact')}
                      className="text-[14px] font-medium text-oak-black/40 transition-opacity hover:text-oak-black/60"
                    >
                      ‹ Back
                    </motion.button>
                  )}
                </AnimatePresence>
                <h2 className="font-serif text-[40px] leading-none tracking-[-1.2px] text-oak-black">
                  Request a Demo
                </h2>
              </div>

              {/* ── Content ────────────────────── */}
              <div className="overflow-y-auto overflow-x-hidden">
                <AnimatePresence mode="wait">

                  {/* ── CALENDAR VIEW ──────────── */}
                  {view === 'calendar' && (
                    <motion.div
                      key="cal"
                      initial={{ opacity: 1, x: 0 }}
                      exit={{ x: '-30%', opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                      className="px-6 pb-6"
                    >
                      {/* Week nav */}
                      <div className="flex items-center justify-between py-4">
                        <button
                          onClick={() => canPrev && setWeekOff(weekOff - 1)}
                          disabled={!canPrev}
                          className="group -m-2 flex items-center justify-center p-2"
                        >
                          <span className={`flex h-8 w-8 items-center justify-center rounded-full text-[20px] transition-colors ${canPrev ? 'text-oak-black group-hover:bg-oak-cream' : 'cursor-default text-oak-black/15'}`}>
                            ‹
                          </span>
                        </button>
                        <span className="text-[15px] font-medium tracking-[-0.2px] text-oak-black">
                          Week of {weekLabel}
                        </span>
                        <button
                          onClick={() => canNext && setWeekOff(weekOff + 1)}
                          disabled={!canNext}
                          className="group -m-2 flex items-center justify-center p-2"
                        >
                          <span className={`flex h-8 w-8 items-center justify-center rounded-full text-[20px] transition-colors ${canNext ? 'text-oak-black group-hover:bg-oak-cream' : 'cursor-default text-oak-black/15'}`}>
                            ›
                          </span>
                        </button>
                      </div>

                      {/* Day pills */}
                      <div className="grid grid-cols-7 gap-1">
                        {days.map((day, i) => {
                          const key = dk(day)
                          const has = avail.has(key)
                          const sel = key === selDay
                          return (
                            <button
                              key={key}
                              onClick={() => has && setSelDay(key)}
                              disabled={!has}
                              className={`flex flex-col items-center rounded-2xl py-2.5 transition-all ${
                                sel
                                  ? 'bg-oak-black text-white'
                                  : has
                                    ? 'cursor-pointer text-oak-black hover:bg-oak-cream'
                                    : 'cursor-default text-oak-black/20'
                              }`}
                            >
                              <span className="text-[11px] font-semibold uppercase tracking-wide">
                                {DAYS[i]}
                              </span>
                              <span className="mt-0.5 text-[18px] font-semibold leading-tight">
                                {day.getDate()}
                              </span>
                              {has && <span className="mt-1 h-1 w-1 rounded-full bg-oak-green" />}
                            </button>
                          )
                        })}
                      </div>

                      {/* Time slots */}
                      {selDay && slots.length > 0 && (
                        <div className="mt-5">
                          <p className="mb-3 text-[13px] font-semibold uppercase tracking-widest text-oak-black/30">
                            Available times
                          </p>
                          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                            {slots.map(t => {
                              const local = selDay ? estToLocal(selDay, t) : null
                              return (
                                <button
                                  key={t}
                                  onClick={() => pickTime(t)}
                                  className="rounded-full border border-oak-black/10 px-2 py-2.5 text-[14px] font-medium tracking-[-0.2px] text-oak-black transition-all hover:border-oak-black/25 hover:bg-oak-cream"
                                >
                                  {local?.time ?? t}
                                </button>
                              )
                            })}
                          </div>
                          {selDay && slots[0] && (
                            <p className="mt-3 text-[12px] text-oak-black/30">
                              Times shown in {estToLocal(selDay, slots[0]).tz}
                            </p>
                          )}
                        </div>
                      )}

                      {selDay && slots.length === 0 && (
                        <p className="mt-8 text-center text-[14px] text-oak-black/35">
                          No availability on this day.
                        </p>
                      )}

                      {!selDay && availLoading && (
                        <p className="mt-8 text-center text-[14px] text-oak-black/35">
                          Loading availability…
                        </p>
                      )}

                      {!selDay && !availLoading && (
                        <p className="mt-8 text-center text-[14px] text-oak-black/35">
                          No availability this week.
                        </p>
                      )}
                    </motion.div>
                  )}

                  {/* ── FORM VIEW ──────────────── */}
                  {view === 'form' && (
                    <motion.div
                      key="form"
                      initial={{ x: '30%', opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      exit={{ x: '30%', opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                      className="px-6 pb-6"
                    >
                      <div className="mt-2 flex flex-col gap-5 md:flex-row">
                        {/* Booking summary */}
                        <div className="shrink-0 rounded-2xl bg-oak-cream p-5 md:w-[180px]">
                          <p className="text-[11px] font-semibold uppercase tracking-widest text-gold-500">
                            Your demo
                          </p>
                          <p className="mt-2 text-[16px] font-semibold leading-tight tracking-[-0.3px] text-oak-black">
                            {dateFmt}
                          </p>
                          <p className="mt-1 text-[15px] font-medium text-oak-black/50">
                            {selDay && selTime ? estToLocal(selDay, selTime).time : selTime}
                            {selDay && selTime && (
                              <span className="ml-1 text-[13px] text-oak-black/35">
                                {estToLocal(selDay, selTime).tz}
                              </span>
                            )}
                          </p>
                          <button
                            onClick={() => { setView('calendar'); setSelTime(null) }}
                            className="mt-4 text-[13px] font-medium text-oak-black/35 underline decoration-oak-black/15 underline-offset-2 transition-colors hover:text-oak-black/50"
                          >
                            Change time
                          </button>
                        </div>

                        {/* Swappable form area */}
                        <div className="relative min-h-[200px] flex-1 overflow-hidden">
                          <AnimatePresence mode="wait" initial={false}>
                            {formSub === 'contact' && (
                              <motion.div
                                key="contact"
                                initial={{ x: '-100%', opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                exit={{ x: '-100%', opacity: 0 }}
                                transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                                className="flex flex-col gap-3"
                              >
                                <input
                                  type="text"
                                  value={name}
                                  onChange={e => setName(e.target.value)}
                                  placeholder="Name *"
                                  className={inputCls}
                                />
                                <input
                                  type="email"
                                  value={email}
                                  onChange={e => setEmail(e.target.value)}
                                  placeholder="Email *"
                                  className={inputCls}
                                />
                                <input
                                  type="text"
                                  value={company}
                                  onChange={e => setCompany(e.target.value)}
                                  placeholder="Community or organization (optional)"
                                  className={inputCls}
                                />
                                <button
                                  onClick={() => setFormSub('message')}
                                  className="mt-1 self-start text-[14px] font-medium text-oak-black/35 transition-colors hover:text-oak-black/50"
                                >
                                  Add a message →
                                </button>
                              </motion.div>
                            )}

                            {formSub === 'message' && (
                              <motion.div
                                key="msg"
                                initial={{ x: '100%', opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                exit={{ x: '100%', opacity: 0 }}
                                transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                                className="flex flex-col gap-3"
                              >
                                <textarea
                                  value={msg}
                                  onChange={e => setMsg(e.target.value)}
                                  placeholder="Tell us about your community or what you'd like to see in the demo..."
                                  rows={5}
                                  className={`${inputCls} resize-none !rounded-2xl`}
                                />
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>

                      {/* Schedule button */}
                      <button
                        onClick={submit}
                        disabled={!name.trim() || !email.trim()}
                        className="mt-6 flex w-full items-center justify-center gap-3 rounded-pill bg-oak-black py-4 text-[16px] font-medium tracking-[-0.4px] text-white transition-opacity hover:opacity-80 disabled:cursor-default disabled:opacity-30"
                      >
                        Schedule
                        <span className="h-7 w-7 rounded-full bg-oak-green" />
                      </button>
                    </motion.div>
                  )}

                  {/* ── SUBMITTING ─────────────── */}
                  {view === 'submitting' && (
                    <motion.div
                      key="spin"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="flex flex-col items-center justify-center px-6 py-20"
                    >
                      <div className="h-8 w-8 animate-spin rounded-full border-[2.5px] border-oak-black/10 border-t-oak-black" />
                      <p className="mt-4 text-[15px] font-medium text-oak-black/40">
                        Scheduling your demo…
                      </p>
                    </motion.div>
                  )}

                  {/* ── THANK YOU ──────────────── */}
                  {view === 'thankyou' && (
                    <motion.div
                      key="ty"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5, ease: [0.38, 0.005, 0.215, 1] as const }}
                      className="flex flex-col items-center justify-center px-6 py-16 text-center"
                    >
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-oak-green/15">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                          <path d="M20 6L9 17l-5-5" stroke="#59B744" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                      <h2 className="mt-6 font-serif text-[clamp(32px,5vw,56px)] leading-[0.95] tracking-[-1.2px] text-oak-black">
                        You&apos;re all set
                      </h2>
                      <p className="mt-4 max-w-[420px] text-[clamp(15px,1.5vw,17px)] leading-[1.5] text-oak-black/55">
                        We&apos;ll reach out to{' '}
                        <span className="font-medium text-oak-black/70">{email}</span>{' '}
                        with next steps.
                      </p>
                      <button
                        onClick={onClose}
                        className="mt-8 rounded-pill border border-oak-black/15 bg-white px-6 py-3 text-[15px] font-medium tracking-[-0.3px] text-oak-black transition-colors hover:bg-oak-cream"
                      >
                        Close
                      </button>
                    </motion.div>
                  )}

                  {/* ── ERROR ──────────────────── */}
                  {view === 'error' && (
                    <motion.div
                      key="err"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.4 }}
                      className="flex flex-col items-center justify-center px-6 py-16 text-center"
                    >
                      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-500/10">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="9.5" stroke="#dc2626" strokeWidth="2" />
                          <path d="M12 7.5v5.5M12 16.5h.01" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" />
                        </svg>
                      </div>
                      <h3 className="mt-5 text-[22px] font-semibold tracking-[-0.5px] text-oak-black">
                        Something went wrong
                      </h3>
                      <p className="mt-2 max-w-[340px] text-[15px] leading-[1.5] text-oak-black/45">
                        We couldn&apos;t send your request. Try again, or email us at{' '}
                        <a href="mailto:info@oakplatforms.com" className="font-medium text-oak-black/70 underline underline-offset-2">info@oakplatforms.com</a>.
                      </p>
                      <div className="mt-6 flex gap-3">
                        <button
                          onClick={submit}
                          className="rounded-pill bg-oak-black px-6 py-3 text-[15px] font-medium tracking-[-0.3px] text-white transition-opacity hover:opacity-80"
                        >
                          Try again
                        </button>
                        <button
                          onClick={onClose}
                          className="rounded-pill border border-oak-black/15 bg-white px-6 py-3 text-[15px] font-medium tracking-[-0.3px] text-oak-black transition-colors hover:bg-oak-cream"
                        >
                          Close
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}
